import { useEffect, useState } from 'react';

interface HeroSectionProps {
  scrollY: number;
}

export default function HeroSection({ scrollY }: HeroSectionProps) {
  const [thunderFlash, setThunderFlash] = useState(false);
  const [runeParticles, setRuneParticles] = useState<Array<{ id: number; x: number; delay: number }>>([]);

  useEffect(() => {
    // Generate rune particles
    const particles = Array.from({ length: 15 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 5
    }));
    setRuneParticles(particles);

    // Thunder flash effect
    const thunderInterval = setInterval(() => {
      setThunderFlash(true);
      setTimeout(() => setThunderFlash(false), 100);
    }, Math.random() * 4000 + 8000);

    return () => clearInterval(thunderInterval);
  }, []);

  const parallaxOffset = scrollY * 0.5;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/1ca6a44d1f8cc39995ee2d56431d319f/caf9ded3c284442aa984c43facabdf01.jpeg"
          alt="Valhalla Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
      </div>

      {/* Northern lights animation */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1/3 opacity-30">
          <div className="aurora-1 absolute w-full h-full bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-emerald-500/30 animate-aurora" />
          <div className="aurora-2 absolute w-full h-full bg-gradient-to-r from-emerald-500/20 via-cyan-500/20 to-blue-500/20 animate-aurora-reverse" />
        </div>
      </div>

      {/* Thunder flash effect */}
      {thunderFlash && (
        <div className="absolute inset-0 bg-white opacity-10 pointer-events-none animate-thunder-flash" />
      )}

      {/* Floating rune particles */}
      {runeParticles.map((particle) => (
        <div
          key={particle.id}
          className="absolute text-[#d4af37] text-2xl opacity-0 animate-float-rune pointer-events-none"
          style={{
            left: `${particle.x}%`,
            bottom: '0',
            animationDelay: `${particle.delay}s`
          }}
        >
          {['ᚠ', 'ᚢ', 'ᚦ', 'ᚨ', 'ᚱ'][particle.id % 5]}
        </div>
      ))}

      {/* Main content */}
      <div 
        className="relative z-10 w-full max-w-7xl mx-auto px-6 text-center"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      >
        {/* Logo - Runic V */}
        <div className="relative mb-8 flex justify-center">
          <div className="relative">
            <img
              src="https://static.readdy.ai/image/1ca6a44d1f8cc39995ee2d56431d319f/fb01cbee94d7b57a811423fde44e0c25.jpeg"
              alt="VALHALLA Logo"
              className="w-48 h-48 object-contain animate-glow-pulse drop-shadow-[0_0_30px_rgba(212,175,55,0.8)]"
            />
          </div>
        </div>

        {/* VALHALLA wordmark */}
        <h1 className="text-8xl font-bold text-white mb-6 tracking-[0.15em] bg-gradient-to-b from-white via-[#d4af37] to-[#ffed4e] bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(212,175,55,0.6)]" style={{ fontFamily: 'Cinzel, serif' }}>
          VALHALLA
        </h1>

        {/* Tagline */}
        <p className="text-[#c0c0c0] text-xl tracking-[0.15em] uppercase mb-12 font-light drop-shadow-lg">
          Where warriors rest. Where the brave get rewarded.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-wrap items-center justify-center gap-6">
          <a
            href="#buy"
            className="group relative px-12 py-4 bg-gradient-to-r from-[#d4af37] to-[#ffed4e] text-black font-bold text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] whitespace-nowrap cursor-pointer flex items-center gap-3"
          >
            BUY
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
          <a
            href="#telegram"
            className="group relative px-12 py-4 bg-black border-2 border-[#d4af37] text-white font-bold text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] whitespace-nowrap cursor-pointer flex items-center gap-3"
          >
            TELEGRAM
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
          <a
            href="#whitepaper"
            className="group relative px-12 py-4 bg-black border-2 border-[#d4af37] text-white font-bold text-lg rounded-full transition-all duration-200 hover:scale-105 hover:shadow-[0_0_25px_rgba(212,175,55,0.6)] whitespace-nowrap cursor-pointer flex items-center gap-3"
          >
            WHITEPAPER
            <i className="ri-arrow-right-line text-xl"></i>
          </a>
        </div>
      </div>

      {/* Vignette effect */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.7)_100%)] pointer-events-none" />

      {/* Scroll indicator */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-[#d4af37] text-4xl"></i>
      </div>
    </section>
  );
}
